import type { AgenticStatusResponse, AgenticTokenExpiry } from "../api/agenticClient";
import { fetchAgenticStatus } from "../api/agenticClient";
import { isRobinhoodIntegrationEnabled } from "./agenticFeatures";

export type RobinhoodBadgeStatus = "ok" | "warning" | "error" | "checking" | "unknown";

export interface RobinhoodStatusResult {
  visible: boolean;
  badgeStatus: RobinhoodBadgeStatus;
  connected: boolean;
  message: string;
}

const STATUS_POLL_MS = 60_000;
const EXPIRING_SOON_SEC = 3600;

let latestResult: RobinhoodStatusResult = {
  visible: false,
  badgeStatus: "unknown",
  connected: false,
  message: "Robinhood",
};

function formatExpiresIn(sec: number): string {
  if (sec >= 86400) return `${Math.ceil(sec / 86400)}d`;
  if (sec >= 3600) return `${Math.ceil(sec / 3600)}h`;
  if (sec >= 60) return `${Math.ceil(sec / 60)}m`;
  return `${Math.max(0, sec)}s`;
}

function isTokenExpiringSoon(expiry: AgenticTokenExpiry | null | undefined): boolean {
  if (!expiry?.accessTokenExpiresAt) return false;
  const sec = expiry.accessTokenExpiresInSec;
  if (typeof sec === "number") return sec <= EXPIRING_SOON_SEC;
  const expiresAtMs = Date.parse(expiry.accessTokenExpiresAt);
  if (!Number.isFinite(expiresAtMs)) return false;
  return expiresAtMs - Date.now() <= EXPIRING_SOON_SEC * 1000;
}

function hiddenRobinhoodStatus(): RobinhoodStatusResult {
  return {
    visible: false,
    badgeStatus: "unknown",
    connected: false,
    message: "Robinhood",
  };
}

export function deriveRobinhoodStatus(status: AgenticStatusResponse): RobinhoodStatusResult {
  if (!status.connected) return hiddenRobinhoodStatus();

  const expiring = isTokenExpiringSoon(status.tokenExpiry);
  const sec = status.tokenExpiry?.accessTokenExpiresInSec;
  const message =
    expiring && typeof sec === "number"
      ? sec > 0
        ? `Robinhood connected — access token expires in ${formatExpiresIn(sec)}`
        : "Robinhood connected — access token expired; will refresh on next use"
      : "Robinhood connected";

  return {
    visible: true,
    badgeStatus: expiring ? "warning" : "ok",
    connected: true,
    message,
  };
}

export function getRobinhoodStatusResult(): RobinhoodStatusResult {
  return latestResult;
}

/** Row menus only offer Open in Robinhood / Legend after this login has connected. */
export function isRobinhoodBrokerConnected(): boolean {
  return isRobinhoodIntegrationEnabled() && latestResult.connected;
}

function setHoverTip(el: HTMLElement, tip: string): void {
  el.title = tip;
  el.setAttribute("data-tip", tip);
}

export function robinhoodStatusIndicatorHtml(opts: { label?: string } = {}): string {
  const label = opts.label ?? "RH";
  return `
    <button type="button" class="sfe-rh-status" data-action="rh-status" hidden aria-label="Robinhood connection status" data-tip="Robinhood — checking…" title="Robinhood — checking…">
      <span class="sfe-rh-dot" data-rh-dot data-status="unknown" aria-hidden="true"></span>
      <span class="sfe-rh-status-label">${label}</span>
    </button>
  `;
}

export function applyRobinhoodStatus(host: ParentNode, result: RobinhoodStatusResult): void {
  latestResult = result;
  const btn = host.querySelector<HTMLElement>("[data-action='rh-status']");
  const dot = host.querySelector<HTMLElement>("[data-rh-dot]");
  if (!btn) return;

  btn.hidden = !result.visible;
  if (dot) dot.dataset.status = result.badgeStatus;
  setHoverTip(btn, result.message);
}

async function loadRobinhoodStatus(): Promise<RobinhoodStatusResult> {
  if (!isRobinhoodIntegrationEnabled()) return hiddenRobinhoodStatus();

  try {
    const status = await fetchAgenticStatus();
    return deriveRobinhoodStatus(status);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Could not read Robinhood status";
    if (!latestResult.visible && !latestResult.connected) return hiddenRobinhoodStatus();
    return {
      visible: true,
      badgeStatus: "error",
      connected: false,
      message,
    };
  }
}

export function startRobinhoodStatusPolling(
  onUpdate: (result: RobinhoodStatusResult) => void,
): () => void {
  if (!isRobinhoodIntegrationEnabled()) {
    onUpdate(hiddenRobinhoodStatus());
    return () => {};
  }

  let cancelled = false;

  const run = async () => {
    if (cancelled) return;
    if (latestResult.visible) {
      onUpdate({
        ...latestResult,
        badgeStatus: "checking",
        message: "Checking Robinhood connection…",
      });
    }
    const result = await loadRobinhoodStatus();
    if (!cancelled) onUpdate(result);
  };

  void run();
  const timer = window.setInterval(() => void run(), STATUS_POLL_MS);

  const onVisible = () => {
    if (document.visibilityState === "visible") void run();
  };
  document.addEventListener("visibilitychange", onVisible);

  const onChanged = () => void run();
  window.addEventListener("sfe:agentic-status-changed", onChanged);

  return () => {
    cancelled = true;
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("sfe:agentic-status-changed", onChanged);
  };
}

export function notifyRobinhoodStatusChanged(): void {
  window.dispatchEvent(new CustomEvent("sfe:agentic-status-changed"));
}
